// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PdpProductPriceAndActions, {
    PdpProductPriceAndActionsStyle,
} from "@ces/App/Components/Product/PdpProductPriceAndActions";
import ProductPartNumbers from "@ces/Overrides/Components/ProductPartNumbers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

type Props = HasProductContext;

export interface ProductDetailsSummaryStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    productImage?: LazyImageProps;
    productTitle?: TypographyPresentationProps;
    brandStyles?: ProductBrandStyles;
    titleWrapper?: InjectableCss;
    actionStyles?: PdpProductPriceAndActionsStyle;
}

export const productDetailsSummaryStyles: ProductDetailsSummaryStyles = {};

const styles = productDetailsSummaryStyles;

const ProductDetailsSummary: FC<Props> = ({ productContext }) => {
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const productImage = product.smallImagePath || product.mediumImagePath || product.largeImagePath;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <LazyImage
                    src={productImage}
                    altText={product.imageAltText || product.productTitle}
                    {...styles.productImage}
                />
                <StyledWrapper {...styles.titleWrapper}>
                    {product.brand && (
                        <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                    )}
                    <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        manufacturerItem={product.manufacturerItem}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <PdpProductPriceAndActions
                    showAddToList={false}
                    showDivider={false}
                    extendedStyles={styles.actionStyles}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductDetailsSummary);
